import { useQueryClient } from "@tanstack/react-query";
import type {
  CompletionsData,
  DueByTypeData,
  DueBySizeData,
  OldestTasksData,
  OverdueByWeekData,
  SpotifyNowPlaying,
  TaskOpsKpiSummary,
  UnblockedOverdueData,
  WeeklyActivityData
} from "@silph-scope/shared";
import { useDashboard, useMe, usePanel, useSpotifyNowPlaying } from "../api/hooks";
import { usePresentMode, usePresentScale } from "../hooks/usePresentMode";
import { apiFetch } from "../api/client";
import { useTheme } from "../theme/ThemeContext";
import { ChartPanel } from "../components/ChartPanel";
import { RosterAdminButton } from "../components/RosterEditor";
import { Fireworks } from "../components/Fireworks";
import {
  completionsOption,
  dueByTypeOption,
  dueBySizeOption,
  overdueByWeekOption,
  unblockedOverdueOption,
  weeklyActivityOption
} from "../theme/chartOptions";

const DASHBOARD_ID = "task-operations";

export function TaskOperationsPage() {
  const { theme } = useTheme();
  const { setPresent } = usePresentMode();
  const presentScale = usePresentScale();
  const queryClient = useQueryClient();
  const { data: dashboard } = useDashboard(DASHBOARD_ID);
  const { data: me } = useMe();
  const interval = dashboard?.refreshInterval ?? "5m";

  const kpi = usePanel<TaskOpsKpiSummary>(DASHBOARD_ID, "kpi-summary", interval);
  const weekly = usePanel<WeeklyActivityData>(DASHBOARD_ID, "weekly-activity", interval);
  const completions = usePanel<CompletionsData>(DASHBOARD_ID, "completions", interval);
  const dueByType = usePanel<DueByTypeData>(DASHBOARD_ID, "due-by-type", interval);
  const dueBySize = usePanel<DueBySizeData>(DASHBOARD_ID, "due-by-size", interval);
  const unblockedOverdue = usePanel<UnblockedOverdueData>(DASHBOARD_ID, "unblocked-overdue", interval);
  const overdueByWeek = usePanel<OverdueByWeekData>(DASHBOARD_ID, "overdue-by-week", interval);
  const oldestTasks = usePanel<OldestTasksData>(DASHBOARD_ID, "oldest-tasks", interval);

  const isAdmin = me?.roles.includes("admin") ?? false;

  async function handleRefresh() {
    await apiFetch(`/api/dashboards/${DASHBOARD_ID}/refresh`, { method: "POST" });
    await queryClient.refetchQueries({ queryKey: ["panel", DASHBOARD_ID] });
  }

  const syncedAt = kpi.data?.syncedAt ? new Date(kpi.data.syncedAt) : null;
  const isStale = kpi.data?.status === "stale" || kpi.data?.status === "failed";
  const dueByTypeData = dueByType.data?.data;

  return (
    <div className="task-ops">
      <header className="page-header">
        <div>
          <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            Dashboards <i className="ri-arrow-right-s-line" /> Operations
          </div>
          <h1>Task Operations</h1>
          <p>Open work, due dates, and delivery risk across all Asana projects.</p>
        </div>
        <div className="header-controls">
          <div className="chip">
            <span className="dot" />
            {syncedAt ? `Live · synced ${relativeTime(syncedAt)}` : "Awaiting first sync"}
          </div>
          <div className="chip">
            <i className="ri-refresh-line" />
            Refresh Rate: <strong>{interval}</strong>
          </div>
          <RosterAdminButton pageId={DASHBOARD_ID} />
          {isAdmin && (
            <button className="btn-primary" onClick={handleRefresh} disabled={kpi.isFetching}>
              <i className="ri-restart-line" />
              Refresh now
            </button>
          )}
          <button className="btn-ghost" type="button" onClick={() => setPresent(true)}>
            <i className="ri-slideshow-line" />
            Present
          </button>
        </div>
      </header>

      {isStale && (
        <div className="stale-banner">
          <i className="ri-error-warning-fill" />
          Data may be out of date — last sync check failed or is stale.
        </div>
      )}

      {kpi.data && <KpiStrip data={kpi.data.data} />}

      <section className="grid-2-1_25">
        <ChartPanel title="Weekly task activity" subtitle="Created · Due · Completed" option={weekly.data ? weeklyActivityOption(theme, weekly.data.data, presentScale) : null} />
        <ChartPanel title="Completions" subtitle="Last 14 days" option={completions.data ? completionsOption(theme, completions.data.data, presentScale) : null} />
      </section>

      <section className="grid-3">
        <ChartPanel
          title="Due this month by type"
          option={dueByTypeData ? (size) => dueByTypeOption(theme, dueByTypeData, presentScale, size) : null}
        />
        <ChartPanel title="Due this week by size" subtitle="Stacked by type" option={dueBySize.data ? dueBySizeOption(theme, dueBySize.data.data, presentScale) : null} />
        <ChartPanel title="Blocked & overdue" subtitle="By size" option={unblockedOverdue.data ? unblockedOverdueOption(theme, unblockedOverdue.data.data, presentScale) : null} />
      </section>

      <section className="grid-2-1_1">
        <ChartPanel
          title="Overdue by week"
          subtitle="By age · next week is a look-ahead"
          option={overdueByWeek.data ? overdueByWeekOption(theme, overdueByWeek.data.data, presentScale) : null}
        />
        <OldestTasksTable data={oldestTasks.data?.data} />
      </section>
    </div>
  );
}

/** Looker chart-level filter on the Overdue tile: drop blocked tasks. */
function overdueExcludingBlocked(overdue: TaskOpsKpiSummary["overdue"]): number {
  return Math.max(0, overdue.value - overdue.blocked);
}

const WEEKDAY_INDEX: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5 };

function todayWeekdayInLosAngeles(): number {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "short"
  }).format(new Date());
  return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekday] ?? 0;
}

/** Weekends have no tile of their own, so every Mon–Fri tile already happened by then. */
function isPastWeekday(day: string): boolean {
  const today = todayWeekdayInLosAngeles();
  const tile = WEEKDAY_INDEX[day];
  if (tile == null) return false;
  return today === 0 || today === 6 ? true : tile < today;
}

function deltaIcon(deltaPct: number): string {
  if (deltaPct > 0) return "ri-arrow-up-line";
  if (deltaPct < 0) return "ri-arrow-down-line";
  return "ri-subtract-line";
}

/** Stacked day-bar segments: on-time, late, blocked (done states + open-blocked), then remaining (still open). */
function daySegments(
  d: TaskOpsKpiSummary["thisWeek"]["days"][number],
  remainingOverdue: boolean
): { key: string; count: number; color: string }[] {
  return [
    { key: "onTime", count: d.onTime, color: "var(--h-ok-fg)" },
    { key: "late", count: d.late, color: "var(--h-warn-fg)" },
    { key: "blocked", count: d.blocked, color: "var(--h-primary)" },
    {
      key: "remaining",
      count: d.remaining,
      color: remainingOverdue ? "var(--h-bad-fg)" : "var(--h-text)"
    }
  ];
}

const OVERDUE_SPARKLES: { x: string; y: string; size: string; delay: string; ok?: boolean }[] = [
  { x: "-14px", y: "-2px", size: "14px", delay: "-1.05s" },
  { x: "40px", y: "4px", size: "11px", delay: "-0.2s", ok: true },
  { x: "46px", y: "36px", size: "8px", delay: "-1.8s" },
  { x: "-4px", y: "48px", size: "10px", delay: "-0.7s", ok: true },
  { x: "16px", y: "-12px", size: "7px", delay: "-1.5s" }
];

function KpiStrip({ data }: { data: TaskOpsKpiSummary }) {
  const overdueCurrent = overdueExcludingBlocked(data.overdue);
  const overdueClear = overdueCurrent === 0;
  const projectedClear = overdueClear && data.overdue.projected === 0;
  const allClear = data.thisWeek.days.every((d) => d.remaining === 0);

  return (
    <>
      {allClear && <Fireworks />}
      <section className="stat-bar">
        <div className="stat-cell stat-total-open-cell">
          <div className="kpi-label">Total open</div>
          <div className="kpi-body">
            <div className="kpi-hero-row">
              <div className="stat-value">{data.totalOpen.value}</div>
              <div className="kpi-hero-aside">
                <span className="stat-delta-inline">
                  <i className={deltaIcon(data.totalOpen.deltaPct)} /> {Math.abs(data.totalOpen.deltaPct)}%
                </span>
                <span className="stat-caption">{data.totalOpen.comparisonLabel}</span>
              </div>
            </div>
          </div>
        </div>

        <div className={`stat-cell stat-overdue-cell${overdueClear ? " is-clear" : ""}`}>
          {overdueClear && (
            <div className="overdue-ring" aria-hidden="true">
              <span className="overdue-ring-spin" />
            </div>
          )}
          <div className="kpi-label">Overdue</div>
          <div className="overdue-live" aria-live="polite" aria-atomic="true">
            {overdueClear ? `Overdue ${overdueCurrent}, clear` : `Overdue ${overdueCurrent}`}
          </div>
          <div className="kpi-body">
            <div className="kpi-hero-row">
              <div className="overdue-hero">
                <div className={`stat-value${overdueClear ? " is-ok" : " is-bad"}`} aria-hidden="true">
                  {overdueCurrent}
                </div>
                {overdueClear &&
                  OVERDUE_SPARKLES.map((spark) => (
                    <span
                      key={`${spark.x}-${spark.y}`}
                      className={`overdue-sparkle${spark.ok ? " is-ok" : ""}`}
                      style={{ left: spark.x, top: spark.y, width: spark.size, height: spark.size, animationDelay: spark.delay }}
                      aria-hidden
                    />
                  ))}
              </div>
              <div
                className="overdue-breakdown"
                aria-label={`${data.overdue.begin} begin plus ${data.overdue.risk} at risk minus ${data.overdue.rescued} rescued equals ${data.overdue.projected} projected`}
              >
                <div className="overdue-row">
                  <span className="overdue-num">{data.overdue.begin}</span>
                  <span className="overdue-name">Begin</span>
                </div>
                <div className="overdue-row">
                  <span className="overdue-num is-risk">{data.overdue.risk}</span>
                  <span className="overdue-name">Risk</span>
                </div>
                <div className="overdue-row">
                  <span className="overdue-num is-rescued">−{data.overdue.rescued}</span>
                  <span className="overdue-name">Saved</span>
                </div>
                <div className="overdue-row overdue-row-result">
                  <span className={`overdue-num${projectedClear ? " is-ok" : " is-bad"}`}>{data.overdue.projected}</span>
                  <span className="overdue-name">Proj.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="stat-cell stat-blocked-cell">
          <div className="kpi-label">Blocked</div>
          <div className="kpi-body">
            <div className="kpi-hero-row">
              <div className="stat-value is-primary">{data.blocked.value}</div>
              <div className="kpi-hero-aside">
                <span className="stat-caption">{data.blocked.pctOverdue}% also overdue</span>
              </div>
            </div>
          </div>
        </div>

        <div className={`stat-cell stat-week-cell${allClear ? " is-all-clear" : ""}`}>
          <div className="kpi-label">This week</div>
          <div className="kpi-body">
            <div className="stat-week-grid">
            {data.thisWeek.days.map((d) => {
              const done = d.remaining === 0;
              const remainingOverdue = !done && isPastWeekday(d.day);
              const segments = daySegments(d, remainingOverdue);
              const total = segments.reduce((sum, s) => sum + s.count, 0);
              return (
                <div className="stat-day" key={d.day}>
                  <div className="stat-day-head">
                    <span className="stat-day-name">{d.day}</span>
                    <span className="stat-day-date">{d.date}</span>
                  </div>
                  <div className="stat-day-top">
                    <div className="stat-day-remain">
                      <b className={done ? "is-done" : remainingOverdue ? "is-late" : undefined}>{d.remaining}</b>
                    </div>
                    <div className="stat-day-details">
                      <div className="stat-day-stats">
                        <span style={{ color: "var(--h-ok-fg)" }}>
                          <b>{d.onTime}</b> On-time
                        </span>
                        <span style={{ color: "var(--h-warn-fg)" }}>
                          <b>{d.late}</b> Late
                        </span>
                        <span style={{ color: "var(--h-primary)" }}>
                          <b>{d.blocked}</b> Blk
                        </span>
                      </div>
                      <div className="stat-day-bar">
                        {total > 0 &&
                          segments
                            .filter((s) => s.count > 0)
                            .map((s) => <span key={s.key} style={{ width: `${(s.count / total) * 100}%`, background: s.color }} />)}
                      </div>
                      <div className="stat-day-age">
                        <span>
                          <b>{d.dueRecent}</b> (0-7d)
                        </span>
                        <span>
                          <b>{d.dueAged}</b> (7d+)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        <SpotifyCard />
      </section>
    </>
  );
}

function SpotifyCard() {
  const { data } = useSpotifyNowPlaying();
  return <SpotifyNowPlayingCard data={data} />;
}

function SpotifyNowPlayingCard({ data }: { data?: SpotifyNowPlaying }) {
  const playing = Boolean(data?.isPlaying && data.title);
  const hasTrack = Boolean(data?.title);
  const progress =
    data?.progressMs != null && data.durationMs ? Math.min(100, (data.progressMs / data.durationMs) * 100) : 0;
  const subtitle = !data?.configured
    ? "Not configured"
    : playing
      ? data.artist
      : hasTrack
        ? "Paused"
        : "Not playing";
  const title = hasTrack ? data!.title : "Spotify";
  const href = data?.trackUrl;

  return (
    <div className={`stat-cell stat-now-cell${playing ? "" : " is-idle"}`}>
      <div className="stat-now-head">
        <div className="kpi-label">Now playing</div>
        <i className="ri-spotify-fill" />
      </div>
      <div className="stat-now-track">
        <div className="spotify-art" aria-hidden>
          {data?.albumArtUrl ? <img src={data.albumArtUrl} alt="" /> : <i className="ri-music-2-line" />}
        </div>
        <div className="stat-now-info">
          {href && hasTrack ? (
            <a className="spotify-track" href={href} target="_blank" rel="noreferrer">
              {title}
            </a>
          ) : (
            <div className="spotify-track">{title}</div>
          )}
          <div className="stat-now-artist">{subtitle}</div>
        </div>
      </div>
      <div className="spotify-bar" aria-hidden>
        <span style={{ width: `${playing ? progress : 0}%` }} />
      </div>
    </div>
  );
}

function OldestTasksTable({ data }: { data?: OldestTasksData }) {
  return (
    <div className="card panel-card table-panel">
      <div className="panel-header">
        <h2>Oldest due tasks</h2>
        <span>Top 10</span>
      </div>
      <div className="table-header-row">
        <span />
        <span>Parsec Partner</span>
        <span>Task</span>
        <span style={{ textAlign: "right" }}>Due</span>
      </div>
      <div className="table-body">
        {(data?.rows ?? []).map((t) => (
          <div className="table-row" key={t.id}>
            <i className="ri-indeterminate-circle-line" style={{ color: "var(--h-bad-fg)" }} />
            <span className="task-partner" title={t.partner}>{t.partner || "—"}</span>
            <span className="task-title">{t.title}</span>
            <span className="due-pill">{t.due}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function relativeTime(date: Date): string {
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
